import { CheckboxGroup as ChakraCheckboxGroup } from '@chakra-ui/react';
import type {
  CheckboxGroupProps as ChakraCheckboxGroupProps,
  CheckboxGroupContext as ChakraCheckboxGroupContext,
  StackProps,
} from '@chakra-ui/react';
import { createContext } from '@chakra-ui/react-context';
import { getValidChildren } from '@chakra-ui/react-utils';
import React from 'react';
import type { Merge } from 'type-fest';

import { Checkbox, Stack } from '@frontend/components';

import {
  SIZE_TO_VERTICAL_SPACING_MAP,
  SIZE_TO_HORIZONTAL_SPACING_MAP,
  SIZE_TO_STACK_MARGIN_MAP,
  DEFAULT_PROPS,
} from './const';
import type { CheckboxSize, ValueType } from './types';
import { checkRecursiveByVal, setRecursiveByVal } from './utils';

export type CheckboxTreeGroupProps = Merge<
  ChakraCheckboxGroupProps,
  {
    size?: CheckboxSize;
    direction?: StackProps['direction'];
    parent?: React.ReactNode;
    value?: ValueType;
    onChange?: (value: ValueType) => void;
  }
>;

export const [CheckboxGroupProvider, useCheckboxGroupContext] =
  createContext<ChakraCheckboxGroupContext>({
    name: 'CheckboxGroupContext',
    strict: false,
  });

export const CheckboxTreeGroup = ({
  size,
  children,
  isDisabled = false,
  direction = DEFAULT_PROPS.direction,
  parent = undefined,
  value: valueProp, // value is stored as an object; keys are each child checkbox's html name; values are booleans.
  onChange,
  ...restProps
}: CheckboxTreeGroupProps) => {
  // Get checkbox group size as 'realSize' if this checkbox group is within another checkbox group.
  const group = useCheckboxGroupContext();

  const defaultSize = DEFAULT_PROPS.size;

  const hasParent = parent !== undefined;
  // This group is 'controlled' if we have a parent element, and/or if we've set a value array.
  const isControlled = hasParent || valueProp;
  const value = valueProp ?? {};
  const allChecked = checkRecursiveByVal(value, true);
  const isIndeterminate = checkRecursiveByVal(value, false) && allChecked;
  const realSize = size ?? group?.size ?? defaultSize;

  return (
    <ChakraCheckboxGroup size={realSize} isDisabled={isDisabled} {...restProps}>
      {/* Add a parent checkbox if specified. */}
      {hasParent && (
        <Checkbox
          isChecked={allChecked}
          isIndeterminate={isIndeterminate}
          mb={SIZE_TO_VERTICAL_SPACING_MAP[realSize] || undefined}
          onChange={(val) => {
            onChange?.(setRecursiveByVal(value, val.target.checked));
          }}
        >
          {parent}
        </Checkbox>
      )}
      <Stack
        pl={hasParent ? SIZE_TO_STACK_MARGIN_MAP[realSize] || undefined : 0}
        direction={direction}
        width="fit-content"
        maxWidth="100%"
        spacing={
          direction === 'row'
            ? SIZE_TO_HORIZONTAL_SPACING_MAP[realSize] || undefined
            : SIZE_TO_VERTICAL_SPACING_MAP[realSize] || undefined // direction == 'column', and default
        }
      >
        {isControlled
          ? getValidChildren(children).map((child) => {
              // Add this object's name as a key value pair to the state if it isn't already there.
              if (value[child.props.name] == null) {
                onChange?.({
                  ...value,
                  [child.props.name]: false,
                });
              }
              const isCheckboxOnly = child.type === Checkbox;
              return React.cloneElement(child, {
                key: child.props.name,
                value: !isCheckboxOnly ? value[child.props.name] : undefined,
                isChecked: isCheckboxOnly ? value[child.props.name] : undefined,
                isDisabled: child.props.isDisabled || isDisabled,
                onChange: (val: any) =>
                  onChange?.({
                    ...value,
                    [child.props.name]: isCheckboxOnly
                      ? val.target.checked
                      : val,
                  }),
              });
            })
          : children}
      </Stack>
    </ChakraCheckboxGroup>
  );
};
